import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { useInfinitePriceAlertEvents, useCreatePriceAlertConfig, usePriceAlertStats } from "@/hooks/usePriceAlerts";
import { PriceAlertEventTile } from "@/components/PriceAlertEventTile";
import { PriceAlertConfigSheetDesktop } from "@/components/PriceAlertConfigSheet.Desktop";
import { PriceAlertAnalyzeSheetDesktop } from "@/components/PriceAlertAnalyzeSheet.Desktop";
import { PositionTradeDrawerDesktop } from "@/pages/alerts/drawers/PositionTradeDrawer.Desktop";
import { CreatePriceAlertConfigPayload, PriceAlertEventData } from "@/api/price-alerts";
import { PriceAlertStatsCards } from "@/components/PriceAlertStatsCards";
import { Loader2, Plus, Settings } from "lucide-react";
import { DesktopLayout } from "@/layouts";
import { ensureUsdtSymbol } from "@/utils/symbols";

const EVENTS_PER_PAGE = 10;

export const PriceAlertsPageDesktop: React.FC = () => {
  const navigate = useNavigate();
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const [showAddSheet, setShowAddSheet] = useState(false);
  const [analyzeEvent, setAnalyzeEvent] = useState<PriceAlertEventData | null>(null);
  const [isTradeDrawerOpen, setIsTradeDrawerOpen] = useState(false);
  const [tradeSymbol, setTradeSymbol] = useState<string | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { data: statsData, isLoading: isLoadingStats } = usePriceAlertStats(userId);

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfinitePriceAlertEvents(userId, EVENTS_PER_PAGE);

  const createMutation = useCreatePriceAlertConfig(userId);

  const events = data?.pages.flatMap((page) => page.events) ?? [];
  const hasMore = hasNextPage ?? false;

  useEffect(() => {
    if (!hasMore || isLoading || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasMore, isLoading, isFetchingNextPage, fetchNextPage]);

  const handleCreateConfig = (payload: CreatePriceAlertConfigPayload) => {
    createMutation.mutate(payload, {
      onSuccess: () => setShowAddSheet(false),
    });
  };

  const handleOpenTrade = (event: PriceAlertEventData) => {
    setTradeSymbol(ensureUsdtSymbol(event.symbol));
    setIsTradeDrawerOpen(true);
  };

  const handleTradeFromAnalyze = (symbol: string) => {
    setTradeSymbol(symbol);
    setAnalyzeEvent(null);
    setIsTradeDrawerOpen(true);
  };

  const headerActions = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => navigate("/price-alerts/manage")}
        className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white font-medium rounded-lg transition-all duration-150 shadow-lg"
      >
        <Settings className="w-4 h-4" />
        <span>Manage Alerts</span>
      </button>
      <button
        onClick={() => setShowAddSheet(true)}
        className="flex items-center gap-2 px-4 py-2 bg-accent hover:bg-accent/80 text-white font-medium rounded-lg transition-all duration-150 shadow-lg hover:shadow-accent/20"
      >
        <Plus className="w-4 h-4" />
        <span>Add Alert</span>
      </button>
    </div>
  );

  return (
    <>
      <DesktopLayout
        pageTitle="Price Alerts"
        pageDescription="Real-time price spike and drop notifications"
        showRightSidebar={false}
        headerActions={headerActions}
      >
        <div className="mb-6">
          <PriceAlertStatsCards stats={statsData} isLoading={isLoadingStats} />
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {!isLoading && events.length === 0 && (
          <div className="text-center py-12 bg-tertiary border border-border rounded-xl">
            <p className="text-text-secondary">No price alerts yet</p>
            <p className="text-text-secondary text-sm mt-1">
              Configure your alerts to start receiving notifications
            </p>
          </div>
        )}

        {!isLoading && events.length > 0 && (
          <div
            className="grid gap-4 justify-center"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(480px, 524px))",
            }}
          >
            {events.map((event) => (
              <div key={event.id} className="w-full max-w-[524px]">
                <PriceAlertEventTile
                  event={event}
                  onAnalyze={setAnalyzeEvent}
                  onTrade={handleOpenTrade}
                />
              </div>
            ))}
          </div>
        )}

        {!isLoading && hasMore && (
          <div ref={loadMoreRef} className="flex justify-center py-8">
            {isFetchingNextPage && (
              <Loader2 className="w-6 h-6 animate-spin text-accent" />
            )}
          </div>
        )}

        {!isLoading && !hasMore && events.length > 0 && (
          <div className="text-center py-8">
            <p className="text-text-secondary text-sm">
              No more alerts to load
            </p>
          </div>
        )}
      </DesktopLayout>

      {/* Add Alert Sheet */}
      <PriceAlertConfigSheetDesktop
        open={showAddSheet}
        onOpenChange={setShowAddSheet}
        onSubmit={(payload) =>
          handleCreateConfig(payload as CreatePriceAlertConfigPayload)
        }
        isLoading={createMutation.isPending}
      />

      {/* Analyze Sheet */}
      <PriceAlertAnalyzeSheetDesktop
        userId={userId}
        event={analyzeEvent}
        open={!!analyzeEvent}
        onOpenChange={(open) => { if (!open) setAnalyzeEvent(null); }}
        onTrade={handleTradeFromAnalyze}
      />

      {/* Trade Sheet */}
      <PositionTradeDrawerDesktop
        userId={userId}
        symbol={tradeSymbol}
        open={isTradeDrawerOpen}
        onOpenChange={(open) => {
          setIsTradeDrawerOpen(open);
          if (!open) setTradeSymbol(null);
        }}
        onGoToPosition={() => {}}
      />
    </>
  );
};
